// Chat screen (the within-block conversation). Twin of the desktop chat
// renderer.
//
//   - The custom start message (when the block enables it) is a persistent
//     header pinned at the top of the chat, NOT a separate "begin" screen.
//   - Bubbles match desktop: user is blue / right / white plain text; assistant
//     is light green / left / black and rendered as Markdown with fenced code
//     blocks (copy button + language badge).
//   - Typing indicator is OFF by default (the reply just appears when ready,
//     like desktop). When the block enables TYPING_INDICATOR it shows the
//     configured style (one of nine) + label, with optional cycling text, and
//     stays visible for at least typing_indicator_min_duration_s so a fast
//     reply does not make it flash.
//   - Text size: when the block enables TEXT_SIZE_CHANGER, bubbles use the
//     configured text_size (in points); otherwise the default size.
//   - Custom chat title: when the block enables CUSTOM_CHAT_TITLE, the browser
//     tab title is set to custom_chat_title for the duration of the block.
//   - Scroll lock: when the block sets scroll_lock, the chat list cannot be
//     scrolled by the participant; it stays pinned to the latest message.
//   - Streaming: when the block enables STREAMING, the reply is revealed line
//     by line as it arrives (an open code fence shows literally until it
//     closes); the complete reply is committed when the turn finishes.
//   - Typewriter: when the block enables TYPEWRITER (and not STREAMING), the
//     reply is typed out after it arrives — Markdown structures appear whole,
//     plain text types character by character. Streaming wins if both are set.
//   - Delay before send (DELAY_BEFORE_SEND): the request is held by a fixed
//     delay after the participant sends (their bubble still appears at once).
//   - Slowdown (SLOWDOWN): during cyclic slow windows (or permanently after a
//     threshold), the reply reveal is held until a minimum delay has passed.
//   - Block messages (BLOCK_MSGS): the input locks for a duration after every
//     Nth reply, then re-enables.
//   - Erase history (ERASE_HISTORY): on a timer, the server is told to forget
//     the turns so far in this block, the visible transcript is cleared, and a
//     "Chat history erased." system line is shown. Stored turns are kept.
//   - The input refocuses when it becomes usable again after a reply.
//   - Web search (WEB_SEARCH): a "Search Web" button sends the turn with a
//     web-search flag; the server grounds the reply in fresh results and wraps
//     it with a header and a Sources list.
//   - Token budget (TOKEN_BUDGET): a char-based budget counter at the top; the
//     server reports tokens_used per turn and ends the block when it is spent.
//   - Inter-trial survey (INTER_TRIAL_SURVEY): every N replies, a survey appears
//     over the chat; answers are logged against the block and the chat resumes.
//   - Timed mid-chat survey (mid_block_surveys "time" trigger): once, N seconds
//     after the chat block starts, the same survey overlay appears. It only
//     shows while the chat is idle; if a reply is in progress it waits for the
//     reply to finish. Unsent typed text stays in the box.
//
// Still later in the chat parity pass (flagged): the manual End & Save button,
// pause-after-completion, clear-display, and voice. We always read the whole
// stream so the server settles the charge.

import { useEffect, useRef, useState } from "react";
import type { WebRendererProps } from "./registry";
import { streamChatTurn, type ChatEvent } from "../session/chatStream";
import {
  eraseChatHistory,
  fetchChatHistory,
  generateAbTurn,
  commitAbChoice,
  requestTts,
  transcribeAudio,
} from "../session/sessionDriver";
import { SessionApiError } from "../api/client";
import { Button } from "../ui/primitives";
import AssistantMarkdown from "./chat/AssistantMarkdown";
import { VoiceRecorder, MAX_RECORDING_MS, MicPermissionError } from "./chat/voiceRecorder";
import { playAudioBlob, type Playback } from "./chat/voicePlayback";
import TypingIndicator from "./chat/TypingIndicator";
import TokenBudgetDisplay from "./chat/TokenBudgetDisplay";
import InterTrialSurvey from "./chat/InterTrialSurvey";
import AbTestDialog from "./chat/AbTestDialog";
import { runTypewriter, segmentForTypewriter, isMarkdownSegment } from "./chat/typewriter";
import { ptToPx, deriveOptionAConfig, deriveOptionBConfig } from "./chat/presentation";
// Re-exported so existing tests can import these from ChatRenderer.
export { segmentForTypewriter, isMarkdownSegment };

type Msg = { id: number; role: "user" | "assistant" | "system"; text: string };
type AbEventLocal = {
  testType: string;
  optionA: string;
  optionB: string;
  revealDelayMs?: number | undefined;
};

type Status = "idle" | "delaying" | "sending" | "blocked" | "error" | "surveying" | "ab";

// Default bubble size when TEXT_SIZE_CHANGER is off: 1.5x the initial 20px, to
// match desktop sizing.
const DEFAULT_BUBBLE_FONT_PX = 30;

// The TYPING_INDICATOR feature flag, including legacy aliases the desktop
// migrates to it (see experiment_manager _FEATURE_MIGRATION).
const TYPING_FLAGS = ["TYPING_INDICATOR", "THINKING", "ELLIPSES"];
export function typingEnabled(features: Record<string, boolean> | undefined): boolean {
  if (!features) return false;
  return TYPING_FLAGS.some((k) => features[k] === true);
}

// Streaming display helpers (twin of desktop streaming_feature.py).
//
// Line-buffered release: the chunk buffer is flushed to the bubble only when a
// newline arrives, and a buffer that is entirely whitespace is skipped (so the
// reply appears line by line, matching desktop _release_buffered_chunk).
export function takeStreamRelease(buffer: string): { released: string; buffer: string } {
  if (!buffer.includes("\n")) return { released: "", buffer };
  if (buffer.trim() === "") return { released: "", buffer: "" };
  return { released: buffer, buffer: "" };
}

// An odd number of code fences means a code block is still open; while it is,
// desktop shows the raw text literally rather than half-rendered Markdown.
export function isFenceOpen(text: string): boolean {
  return (text.split("```").length - 1) % 2 === 1;
}

// --- Slowdown (SLOWDOWN) timing helpers (twin of desktop chat_renderer
// _slowdown_permanent_active / _in_slow_window / _prepare_turn_slowdown).
// elapsedS is seconds since the chat block started. ---
export type SlowdownConfig = {
  periodS: number;
  windowS: number;
  minDelayS: number;
  permEnabled: boolean;
  permAfterS: number;
};

export function readSlowdownConfig(settings: Record<string, unknown>): SlowdownConfig {
  const num = (k: string, d: number) =>
    typeof settings[k] === "number" ? (settings[k] as number) : d;
  return {
    periodS: num("slowdown_period_s", 100),
    windowS: num("slowdown_window_s", 20),
    minDelayS: num("slowdown_min_delay_s", 4),
    permEnabled: settings.slowdown_permanent_after_enabled === true,
    permAfterS: num("slowdown_permanent_after_s", 600),
  };
}

export function slowdownPermanentActive(elapsedS: number, c: SlowdownConfig): boolean {
  if (!c.permEnabled) return false;
  return elapsedS >= c.permAfterS;
}

export function inSlowWindow(elapsedS: number, c: SlowdownConfig): boolean {
  const period = Math.max(1, Math.trunc(c.periodS));
  const window = Math.max(0, Math.trunc(c.windowS));
  if (window <= 0) return false;
  const total = period + window;
  return elapsedS % total >= period;
}

// The earliest timestamp (ms) the reply may be revealed, or null if this turn
// is not slowed. sentTs and sessionStart are ms (Date.now()).
export function slowdownEarliestDisplayTs(
  sentTs: number,
  sessionStart: number,
  c: SlowdownConfig,
): number | null {
  const elapsedS = (sentTs - sessionStart) / 1000;
  const must = slowdownPermanentActive(elapsedS, c) || inSlowWindow(elapsedS, c);
  return must ? sentTs + c.minDelayS * 1000 : null;
}

// --- Timed mid-chat surveys (twin of desktop MidBlockSurveySchedule, "time"
// kind). A time trigger fires ONCE when the seconds since the chat block
// started reach its value. Only an explicit "time" kind counts here: a chat
// trigger with no kind has always meant "every N messages" online. ---
export type ChatTimeTrigger = { id: string; surveyId: string; value: number };

export function readTimeTriggers(settings: Record<string, unknown>): ChatTimeTrigger[] {
  const raw = Array.isArray(settings.mid_block_surveys)
    ? (settings.mid_block_surveys as Array<Record<string, unknown>>)
    : [];
  return raw
    .filter(
      (t) =>
        t != null &&
        t.trigger_kind === "time" &&
        typeof t.value === "number" &&
        t.value > 0 &&
        Boolean(t.survey_id),
    )
    .map((t) => ({
      id: typeof t.id === "string" ? t.id : "",
      surveyId: String(t.survey_id),
      value: t.value as number,
    }))
    .sort((a, b) => a.value - b.value);
}

// Index of the earliest not-yet-fired trigger the clock has reached, or -1.
// triggers must be sorted by value (readTimeTriggers does this).
export function nextDueTimeTrigger(
  triggers: ChatTimeTrigger[],
  fired: Set<number>,
  elapsedS: number,
): number {
  for (let i = 0; i < triggers.length; i++) {
    if (fired.has(i)) continue;
    return elapsedS >= triggers[i].value ? i : -1;
  }
  return -1;
}

// --- Block messages (BLOCK_MSGS) trigger (twin of desktop _start_block gate).
// count is the number of assistant replies so far in this block. ---
export function blockShouldTrigger(count: number, threshold: number, repeat: boolean): boolean {
  if (threshold <= 0) return false;
  if (repeat) return count > 0 && count % threshold === 0;
  return count === threshold;
}

function newTurnId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `turn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// TTS error codes that mean voice is simply not available for this study, so
// the run station should stop trying and fall back to plain text.
const VOICE_OFF_CODES = new Set([
  "no_voice_subscription",
  "model_not_in_tier",
  "insufficient_voice_credits",
  "no_voice",
]);

function fmtSecs(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

export default function ChatRenderer({ step, session, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;
  const features = step.features;

  // --- Voice (VOICE_CONVERSATION). Settings are authored in the desktop
  // designer; the run station only honors them. ---
  const voiceOn = Boolean(features?.VOICE_CONVERSATION);
  const sttEnabled = Boolean(settings.voice_stt_enabled);
  const voiceOnlyMode = settings.voice_display_mode === "voice_only";

  // --- Start message header (persistent, top of chat) ---
  const showStart = Boolean(settings.show_start_message);
  const startText =
    typeof settings.start_message_text === "string" ? settings.start_message_text : "";
  const startFontPt =
    typeof settings.start_message_font_size === "number" ? settings.start_message_font_size : 18;
  const startPx = ptToPx(startFontPt);
  const startColor =
    typeof settings.start_message_color === "string" && settings.start_message_color
      ? settings.start_message_color
      : "#212529";

  // --- Text size (TEXT_SIZE_CHANGER): honor text_size (pt) when enabled ---
  const textSizeOn = features?.["TEXT_SIZE_CHANGER"] === true;
  const textSizePt = typeof settings.text_size === "number" ? settings.text_size : 20;
  const bubbleFontPx = textSizeOn ? ptToPx(textSizePt) : DEFAULT_BUBBLE_FONT_PX;

  // --- Custom chat title (CUSTOM_CHAT_TITLE): set the browser tab title ---
  const customTitleOn = features?.["CUSTOM_CHAT_TITLE"] === true;
  const customTitle =
    typeof settings.custom_chat_title === "string" ? settings.custom_chat_title : "";
  useEffect(() => {
    if (!customTitleOn || !customTitle) return;
    const previous = document.title;
    document.title = customTitle;
    return () => {
      document.title = previous;
    };
  }, [customTitleOn, customTitle]);

  // --- Scroll lock (scroll_lock setting): no participant scrolling ---
  const scrollLocked = settings.scroll_lock === true;

  // --- Typing indicator config ---
  const indicatorOn = typingEnabled(step.features);
  const indicatorStyle =
    typeof settings.typing_indicator_style === "string" ? settings.typing_indicator_style : "dots";
  const indicatorLabel =
    typeof settings.typing_indicator_label === "string"
      ? settings.typing_indicator_label
      : "Thinking";
  const cycle = {
    enabled: Boolean(settings.typing_indicator_cycle_enabled),
    texts: Array.isArray(settings.typing_indicator_cycle_texts)
      ? (settings.typing_indicator_cycle_texts as string[])
      : [],
    intervalS:
      typeof settings.typing_indicator_cycle_interval_s === "number"
        ? settings.typing_indicator_cycle_interval_s
        : 3,
    random: Boolean(settings.typing_indicator_cycle_random),
  };
  // Minimum time the indicator stays up before the reply is revealed (matches
  // desktop _on_response). Only meaningful when the indicator is on.
  const minDurationS =
    typeof settings.typing_indicator_min_duration_s === "number"
      ? settings.typing_indicator_min_duration_s
      : 2;
  const minDurationMs = indicatorOn ? Math.max(0, minDurationS * 1000) : 0;

  // --- Streaming (STREAMING): reveal the reply line by line as it arrives ---
  const streamingMode = features?.["STREAMING"] === true;

  // --- Typewriter (TYPEWRITER): type the reply out after it arrives. Mutually
  // exclusive with streaming on desktop; streaming wins if both are set. ---
  const typewriterMode = features?.["TYPEWRITER"] === true && !streamingMode;
  const typewriterSpeedMs = Math.max(
    1,
    typeof settings.typewriter_speed_ms === "number" ? settings.typewriter_speed_ms : 20,
  );

  // --- Delay before send (DELAY_BEFORE_SEND): hold the outgoing request by a
  // fixed delay (the participant's bubble still appears immediately). ---
  const delayBeforeSendOn = features?.["DELAY_BEFORE_SEND"] === true;
  const delaySeconds =
    typeof settings.delay_seconds === "number" ? settings.delay_seconds : 2;

  // --- Slowdown (SLOWDOWN): hold the reply reveal during slow windows. ---
  const slowdownOn = features?.["SLOWDOWN"] === true;
  const slowdownConfig = readSlowdownConfig(settings);

  // --- Block messages (BLOCK_MSGS): lock the input after every Nth reply. ---
  const blockMsgsOn = features?.["BLOCK_MSGS"] === true;
  const blockThreshold =
    typeof settings.block_message_count === "number" ? settings.block_message_count : 5;
  const blockRepeat = settings.block_repeat !== false; // default true
  const blockDurationMs = Math.max(
    0,
    (typeof settings.block_duration_s === "number" ? settings.block_duration_s : 15) * 1000,
  );

  // --- Erase history (ERASE_HISTORY): on a timer, ask the server to forget the
  // turns so far, then clear the visible transcript and post a system notice.
  // First fire after the delay; if repeat is on, again every interval. ---
  const eraseOn = features?.["ERASE_HISTORY"] === true;
  const eraseDelayMs = Math.max(
    0,
    (typeof settings.erase_history_delay_s === "number" ? settings.erase_history_delay_s : 60) * 1000,
  );
  const eraseRepeat = settings.erase_history_repeat === true; // default false
  const eraseIntervalMs = Math.max(
    1000,
    (typeof settings.erase_history_interval_s === "number" ? settings.erase_history_interval_s : 120) *
      1000,
  );

  // --- Web search (WEB_SEARCH): a "Search Web" button grounds the reply in
  // fresh web results (the server runs the search and wraps the reply). ---
  const webSearchOn = features?.["WEB_SEARCH"] === true;

  // --- Token budget (TOKEN_BUDGET): a char-based budget counter. The server
  // reports tokens_used per turn; the display shows remaining = budget - used.
  // At zero the input locks (the depleting turn also ends the block). ---
  const tokenBudgetOn = features?.["TOKEN_BUDGET"] === true;
  const tokenBudgetAmount =
    typeof settings.token_budget_amount === "number" ? settings.token_budget_amount : 1000;
  const tokenDisplayType =
    typeof settings.token_display_type === "string" ? settings.token_display_type : "numeric_label";

  // --- Inter-trial survey (INTER_TRIAL_SURVEY): show a survey over the chat
  // every N replies, after an optional delay. P3 unified this onto the shared
  // mid_block_surveys COUNT trigger; read it first, falling back to the legacy
  // feature flag + survey_trigger_count for pre-P3 experiments. The survey
  // CONTENT is resolved server-side (GET /chat/survey); here we only need the
  // cadence + whether it's on. Delay + scroll-lock stay legacy block settings. ---
  const midBlockSurveys = Array.isArray(settings.mid_block_surveys)
    ? (settings.mid_block_surveys as Array<Record<string, unknown>>)
    : [];
  const unifiedCountTrigger = midBlockSurveys.find(
    (t) =>
      (t.trigger_kind ?? "count") === "count" &&
      typeof t.value === "number" &&
      t.value > 0 &&
      Boolean(t.survey_id),
  );
  const interTrialOn =
    unifiedCountTrigger != null || features?.["INTER_TRIAL_SURVEY"] === true;
  const surveyTriggerCount = unifiedCountTrigger
    ? (unifiedCountTrigger.value as number)
    : typeof settings.survey_trigger_count === "number"
      ? settings.survey_trigger_count
      : 0;
  const surveyDelaySeconds =
    typeof settings.survey_delay_before_show_seconds === "number"
      ? settings.survey_delay_before_show_seconds
      : 0;
  const surveyScrollLock = settings.inter_trial_scroll_lock === true;
  // Timed mid-chat surveys ("time" triggers), sorted by when they are due.
  const timeTriggers = readTimeTriggers(settings);

  // --- A/B testing (AB_TESTING / AB_UI_TEST / AB_UI_ALT): every Nth message,
  // show two candidate replies and let the participant pick one. Per-side
  // presentation comes from the block (Option A) and the _b settings (Option B).
  const abContentOn = features?.["AB_TESTING"] === true;
  const abUiTestOn = features?.["AB_UI_TEST"] === true;
  const abUiAltOn = features?.["AB_UI_ALT"] === true;
  const abOn = abContentOn || abUiTestOn || abUiAltOn;
  const abTestType: "content" | "ui_test" | "ui_alt" = abUiAltOn
    ? "ui_alt"
    : abUiTestOn
      ? "ui_test"
      : "content";
  const abThreshold =
    typeof settings.ab_test_message_threshold === "number" ? settings.ab_test_message_threshold : 5;
  const abDialogMessage =
    typeof settings.ab_dialog_message === "string" && settings.ab_dialog_message
      ? settings.ab_dialog_message
      : "Which response do you prefer?";
  const abRawA = deriveOptionAConfig(settings, features);
  const abRawB = deriveOptionBConfig(settings);

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  // Char-based tokens spent so far in this block (authoritative, from the server).
  const [tokensUsed, setTokensUsed] = useState(0);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const pendingTurn = useRef<{
    turnId: string;
    message: string;
    forceWebSearch: boolean;
    kind: "text" | "ab";
  } | null>(null);
  const nextId = useRef(1);
  // TTS is the only charged path with no backend idempotency, so it must speak at
  // most once per reply. Keyed on the SERVER's assistant_count (stable across a
  // replayed done_event), NOT the client assistantCountRef (which increments on
  // every commit, including replays).
  const lastSpokenServerCountRef = useRef<number>(0);
  // Synchronous in-flight lock: blocks a same-task/programmatic double-fire of a
  // send before React's state flush can update `status`. Released on every
  // terminal path (success, error, A/B commit error).
  const inFlightRef = useRef(false);
  // Synchronous guard so two rapid A/B picks can't both commit.
  const abSelectingRef = useRef(false);
  // Voice state. voiceDisabled latches once TTS reports voice isn't available,
  // so we stop retrying and fall back to text (even in voice-only mode).
  const [voiceDisabled, setVoiceDisabled] = useState(false);
  const [recordState, setRecordState] = useState<"idle" | "recording" | "transcribing">("idle");
  const [recordSecs, setRecordSecs] = useState(0);
  const [micDenied, setMicDenied] = useState(false);
  const [lastTtsBlob, setLastTtsBlob] = useState<Blob | null>(null);
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const recordIntervalRef = useRef<number | null>(null);
  const ttsPlaybackRef = useRef<Playback | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Streaming state: the running buffer + accumulated released text live in
  // refs (read/written synchronously across chunk events); streamingText is the
  // rendered mirror. null means no stream is in progress.
  const streamBufRef = useRef("");
  const streamAccumRef = useRef("");
  const [streamingText, setStreamingText] = useState<string | null>(null);
  // Typewriter state: the accumulated typed text (null = not typing) and the
  // pending tick timer (cleared on unmount).
  const [typewriterText, setTypewriterText] = useState<string | null>(null);
  const typewriterTimerRef = useRef<number | null>(null);
  // Slowdown reference: when this chat block started (mirrors desktop
  // session_start_time, set at chat-renderer creation).
  const sessionStartRef = useRef<number>(Date.now());
  // Number of assistant replies committed in this block (drives block-messages).
  const assistantCountRef = useRef(0);
  // Participant messages sent in this block; every abThreshold-th one is an A/B trigger.
  const abMsgCountRef = useRef(0);
  // Inter-trial survey: whether the overlay is up, what to do after it closes
  // (resume the chat or advance the ended block), and the pre-show delay timer.
  const [surveyActive, setSurveyActive] = useState(false);
  // The active A/B trial (the two candidates + the user message), or null.
  const [abTrial, setAbTrial] = useState<
    {
      message: string;
      turnId: string;
      optionA: string;
      optionB: string;
      testType: string;
      revealDelayMs?: number | undefined;
    } | null
  >(null);
  const pendingSurveyAfter = useRef<"advance" | "resume" | null>(null);
  // Timed mid-chat surveys: which have fired (by index into timeTriggers), and
  // the one whose survey is showing now (null = the message-count survey).
  const firedTimeTriggersRef = useRef<Set<number>>(new Set());
  const [activeTimeTrigger, setActiveTimeTrigger] = useState<ChatTimeTrigger | null>(null);
  const surveyDelayTimerRef = useRef<number | null>(null);
  // Pending pre-send-delay and input-block timers (cleared on unmount).
  const delayTimerRef = useRef<number | null>(null);
  const blockTimerRef = useRef<number | null>(null);
  // Pending erase-history timer (cleared on unmount).
  const eraseTimerRef = useRef<number | null>(null);
  // The input element, refocused when it becomes usable again after a reply.
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Previous status, so we can refocus only on the transition back to idle.
  const prevStatusRef = useRef<Status>("idle");

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status, streamingText, typewriterText]);

  useEffect(() => {
    return () => {
      if (typewriterTimerRef.current !== null) clearTimeout(typewriterTimerRef.current);
      if (delayTimerRef.current !== null) clearTimeout(delayTimerRef.current);
      if (blockTimerRef.current !== null) clearTimeout(blockTimerRef.current);
      if (eraseTimerRef.current !== null) clearTimeout(eraseTimerRef.current);
      if (surveyDelayTimerRef.current !== null) clearTimeout(surveyDelayTimerRef.current);
      if (recordIntervalRef.current !== null) clearInterval(recordIntervalRef.current);
      ttsPlaybackRef.current?.stop();
      void recorderRef.current?.stop();
    };
  }, []);

  // Seed the screen on block entry from the server's display run: prior chat
  // blocks carry over unless this block has clear-display on (the server
  // decides). Also restores the conversation after a reload. Only seeds while
  // the screen is still empty, so it never clobbers a message already sent.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchChatHistory(session);
        if (cancelled) return;
        const seeded = (res.messages ?? [])
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ id: nextId.current++, role: m.role as Msg["role"], text: m.content }));
        setMessages((cur) => (cur.length === 0 && seeded.length > 0 ? seeded : cur));
      } catch {
        // No history (or the fetch failed): leave the screen empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Erase-history timer: fire after the delay, repeating on the interval when
  // enabled. On fire, tell the server to forget, then clear the local view.
  useEffect(() => {
    if (!eraseOn) return;
    let cancelled = false;
    const fire = async () => {
      if (cancelled) return;
      try {
        await eraseChatHistory(session);
      } catch {
        // If the server erase fails, still clear locally so the participant
        // sees the reset; the next turn reconciles with the server.
      }
      if (cancelled) return;
      setMessages([{ id: nextId.current++, role: "system", text: "Chat history erased." }]);
      if (eraseRepeat && !cancelled) {
        eraseTimerRef.current = window.setTimeout(() => void fire(), eraseIntervalMs);
      }
    };
    eraseTimerRef.current = window.setTimeout(() => void fire(), eraseDelayMs);
    return () => {
      cancelled = true;
      if (eraseTimerRef.current !== null) {
        clearTimeout(eraseTimerRef.current);
        eraseTimerRef.current = null;
      }
    };
  }, [eraseOn, eraseDelayMs, eraseRepeat, eraseIntervalMs, session]);

  // Refocus the input when it becomes usable again after a reply (sending ->
  // idle) or after a message-block lock ends (blocked -> idle).
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (status === "idle" && (prev === "sending" || prev === "blocked" || prev === "surveying")) {
      inputRef.current?.focus();
    }
  }, [status]);

  // Put the survey overlay up (after the optional pre-show delay). Shared by the
  // message-count path and the timed path; status "surveying" locks the input
  // and keeps any block end waiting until handleSurveyClose runs.
  const showSurvey = (after: "advance" | "resume", timeTrigger: ChatTimeTrigger | null) => {
    pendingSurveyAfter.current = after;
    setActiveTimeTrigger(timeTrigger);
    setStatus("surveying");
    if (surveyDelaySeconds > 0) {
      surveyDelayTimerRef.current = window.setTimeout(() => {
        surveyDelayTimerRef.current = null;
        setSurveyActive(true);
      }, surveyDelaySeconds * 1000);
    } else {
      setSurveyActive(true);
    }
  };

  const pushMsg = (role: Msg["role"], text: string) =>
    setMessages((m) => [...m, { id: nextId.current++, role, text }]);

  const onStreamChunk = (chunk: string) => {
    streamBufRef.current += chunk;
    const { released, buffer } = takeStreamRelease(streamBufRef.current);
    streamBufRef.current = buffer;
    if (released) {
      streamAccumRef.current += released;
      setStreamingText(streamAccumRef.current);
    }
  };

  // Type the reply out segment by segment (mirrors desktop _type_step): a
  // Markdown structure (code, bold, list, etc.) appears whole; plain text is
  // typed one character per tick. The buffer is re-rendered as Markdown each
  // tick. Resolves when the whole reply has been typed.
  const typeOut = (reply: string, speedMs: number): Promise<void> =>
    runTypewriter(reply, speedMs, setTypewriterText, (id) => {
      typewriterTimerRef.current = id;
    });

  // Shared post-reply step for both a normal turn and an A/B commit: record the
  // reply, then run the inter-trial survey check, the block advance, and the
  // block-messages lock in that order. Both callers reach this once they have a
  // committed reply text, a terminal disposition, and the server's reply count.
  const afterReplyCommitted = (
    reply: string,
    terminal: "advance" | "stay",
    nextAssistantCount: number | null,
  ) => {
    // The turn's charged work is done: release the in-flight lock so the next
    // send is accepted (status/survey gates still apply for surveying/blocked).
    inFlightRef.current = false;
    if (reply) pushMsg("assistant", reply);
    pendingTurn.current = null;
    if (reply) assistantCountRef.current += 1;
    // Speak at most once per reply. nextAssistantCount is the SERVER's count: a
    // replayed done_event (retry re-sync, or a 2nd A/B commit) carries the SAME
    // count, so the guard skips the duplicate speak -> no re-charge.
    if (reply && voiceOn && !voiceDisabled) {
      const id = nextAssistantCount;
      if (id == null || id > lastSpokenServerCountRef.current) {
        if (id != null) lastSpokenServerCountRef.current = id;
        void playReply();
      }
    }

    // Inter-trial survey: every survey_trigger_count replies, show the survey
    // BEFORE any block-ending advance. Prefer the server's reply count
    // (erase/reload safe); fall back to the client's own committed-reply count.
    const replyCount = nextAssistantCount ?? assistantCountRef.current;
    const surveyDue =
      interTrialOn &&
      surveyTriggerCount > 0 &&
      replyCount > 0 &&
      replyCount % surveyTriggerCount === 0;
    if (surveyDue) {
      showSurvey(terminal === "advance" ? "advance" : "resume", null);
      return;
    }

    if (terminal === "advance") {
      onDone({ kind: "advanced" });
      return;
    }

    // Block messages: lock the input for the configured duration once the reply
    // count reaches the threshold (and only when the block is not finishing).
    if (blockMsgsOn && blockShouldTrigger(assistantCountRef.current, blockThreshold, blockRepeat)) {
      setStatus("blocked");
      blockTimerRef.current = window.setTimeout(() => {
        blockTimerRef.current = null;
        setStatus("idle");
      }, blockDurationMs);
      return;
    }

    setStatus("idle");
  };

  async function runTurn(
    turnId: string,
    message: string,
    end: boolean,
    sentTs: number,
    forceWebSearch = false,
  ) {
    const startedAt = Date.now();
    // Slowdown decides, at send time, the earliest the reply may be revealed.
    const slowdownEarliest = slowdownOn
      ? slowdownEarliestDisplayTs(sentTs, sessionStartRef.current, slowdownConfig)
      : null;
    setStatus("sending");
    setErrorMsg("");
    if (streamingMode) {
      streamBufRef.current = "";
      streamAccumRef.current = "";
      setStreamingText(null);
    }
    let reply = "";
    let terminal: "advance" | "stay" | "error" | null = null;
    let termError = "";
    let nextTokensUsed: number | null = null;
    let nextAssistantCount: number | null = null;
    let nextReplyDelayMs: number | null = null;
    // A scripted A/B step arrives as an "ab" event instead of a reply.
    // Holder object: TS keeps an object property's declared type across a
    // closure assignment (a plain let would narrow to its null initializer).
    const abHolder: { value: AbEventLocal | null } = { value: null };

    try {
      await streamChatTurn(session, { turnId, message, end, forceWebSearch }, (e: ChatEvent) => {
        if (e.type === "chunk") {
          reply += e.text; // full reply (revealed whole on done in default mode)
          if (streamingMode) onStreamChunk(e.text);
        } else if (e.type === "done") {
          terminal = e.block_complete ? "advance" : "stay";
          if (typeof e.tokens_used === "number") nextTokensUsed = e.tokens_used;
          if (typeof e.assistant_count === "number") nextAssistantCount = e.assistant_count;
          if (typeof e.reply_delay_ms === "number") nextReplyDelayMs = e.reply_delay_ms;
        } else if (e.type === "ab") {
          abHolder.value = {
            testType: typeof e.test_type === "string" ? e.test_type : "scripted",
            optionA: typeof e.option_a === "string" ? e.option_a : "",
            optionB: typeof e.option_b === "string" ? e.option_b : "",
            revealDelayMs: typeof e.reply_delay_ms === "number" ? e.reply_delay_ms : undefined,
          };
        } else if (e.type === "ended") terminal = "advance";
        else if (e.type === "error") {
          terminal = "error";
          termError = e.message;
        }
      });
    } catch (err) {
      const msg =
        err instanceof SessionApiError && err.message
          ? err.message
          : "Something went wrong. Please try again.";
      setErrorMsg(msg);
      setStatus("error");
      if (streamingMode) setStreamingText(null);
      inFlightRef.current = false; // released: pendingTurn stays set for onRetry
      return;
    }

    if (terminal === "error") {
      setErrorMsg(termError || "The message did not go through. Please try again.");
      setStatus("error");
      if (streamingMode) setStreamingText(null);
      inFlightRef.current = false; // released: pendingTurn stays set for onRetry
      return;
    }

    // Update the token counter from the server's authoritative count.
    if (nextTokensUsed !== null) setTokensUsed(nextTokensUsed);

    // Scripted A/B step: the server returned two candidates instead of a reply.
    // Open the comparison dialog (the scripted delay becomes its reveal delay).
    const capturedAb = abHolder.value;
    if (capturedAb) {
      setAbTrial({
        message,
        turnId,
        optionA: capturedAb.optionA,
        optionB: capturedAb.optionB,
        testType: capturedAb.testType,
        revealDelayMs: capturedAb.revealDelayMs,
      });
      setStatus("ab");
      return;
    }

    // Keep the typing indicator up for at least its minimum duration before
    // revealing the reply, so a fast reply does not make it flash. Status stays
    // "sending" (the indicator is rendered while sending) during this wait.
    if (!streamingMode && indicatorOn && reply) {
      const remaining = minDurationMs - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    }

    // Scripted reply delay: hold the reveal for the server-sent delay (the
    // typing indicator shows during the wait if the block enables it).
    if (!streamingMode && reply && nextReplyDelayMs !== null && nextReplyDelayMs > 0) {
      const remaining = nextReplyDelayMs - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    }

    // Slowdown: during a slow window, hold the reveal until the earliest
    // display time computed at send. Streaming reveals live, so skip it there
    // (matches desktop, where streaming bypasses the delayed-display path).
    if (!streamingMode && slowdownEarliest !== null && reply) {
      const remaining = slowdownEarliest - Date.now();
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    }

    // Typewriter: type the complete reply out (the live bubble shows the buffer
    // as it grows). Streaming wins if both are set, so this only runs otherwise.
    if (typewriterMode && reply) {
      await typeOut(reply, typewriterSpeedMs);
    }

    // The live (streaming/typewriter) bubble is replaced by the committed
    // message; we commit the COMPLETE reply (never dropping content).
    if (streamingMode) setStreamingText(null);
    if (typewriterMode) setTypewriterText(null);

    afterReplyCommitted(reply, terminal === "advance" ? "advance" : "stay", nextAssistantCount);
  }

  // A/B trigger: ask the server for two candidates, then show the dialog. The
  // server charges the wallet for the model calls; nothing is stored until the
  // participant picks (onAbSelect commits the choice).
  async function runAbTurn(turnId: string, message: string) {
    setStatus("sending");
    setErrorMsg("");
    let result: Awaited<ReturnType<typeof generateAbTurn>>;
    try {
      result = await generateAbTurn(session, { turnId, message });
    } catch (err) {
      const msg =
        err instanceof SessionApiError && err.message
          ? err.message
          : "Something went wrong. Please try again.";
      setErrorMsg(msg);
      setStatus("error");
      inFlightRef.current = false; // released: pendingTurn (kind "ab") stays for onRetry
      return;
    }
    if (result.ended) {
      // Server ended the session (cap reached / wallet empty): advance out.
      afterReplyCommitted("", "advance", null);
      return;
    }
    setAbTrial({
      message,
      turnId,
      optionA: result.option_a ?? "",
      optionB: result.option_b ?? "",
      testType: abTestType,
    });
    setStatus("ab");
  }

  // Commit the participant's A/B choice: the chosen reply becomes the turn and
  // flows through the same post-reply path as a normal reply.
  async function onAbSelect(selection: "A" | "B", latencyMs: number) {
    if (abSelectingRef.current) return; // a pick is already committing
    const trial = abTrial;
    if (!trial) return;
    abSelectingRef.current = true;
    setAbTrial(null);
    setStatus("sending");
    let res: Awaited<ReturnType<typeof commitAbChoice>>;
    try {
      res = await commitAbChoice(session, {
        message: trial.message,
        selection,
        option_a: trial.optionA,
        option_b: trial.optionB,
        test_type: trial.testType,
        latencyMs,
        turnId: trial.turnId,
      });
    } catch (err) {
      const msg =
        err instanceof SessionApiError && err.message
          ? err.message
          : "Something went wrong. Please try again.";
      setErrorMsg(msg);
      setStatus("error");
      inFlightRef.current = false; // released: pendingTurn (kind "ab") stays for onRetry
      abSelectingRef.current = false;
      return;
    }
    if (typeof res.tokens_used === "number") setTokensUsed(res.tokens_used);
    const chosen = selection === "A" ? trial.optionA : trial.optionB;
    afterReplyCommitted(
      chosen,
      res.block_complete ? "advance" : "stay",
      typeof res.assistant_count === "number" ? res.assistant_count : null,
    );
    abSelectingRef.current = false; // commit settled
  }

  const startTurn = (forceWebSearch: boolean, explicitText?: string) => {
    const text = (explicitText ?? input).trim();
    if (!text || status !== "idle") return;
    // Hard synchronous double-fire guard: blocks a same-task/programmatic second
    // send before React flushes `status`. Released on every terminal path.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    pushMsg("user", text);
    setInput("");
    const turnId = newTurnId();
    abMsgCountRef.current += 1;
    // Every abThreshold-th message is an A/B trigger (5th, 10th, ...). The A/B
    // path bypasses the normal pre-send delay; per-side timing lives in the
    // comparison dialog instead. pendingTurn is set (kind "ab") so onRetry can
    // re-run the A/B path and replay the already-charged generate for free.
    if (abOn && abThreshold > 0 && abMsgCountRef.current % abThreshold === 0) {
      pendingTurn.current = { turnId, message: text, forceWebSearch, kind: "ab" };
      void runAbTurn(turnId, text);
      return;
    }
    pendingTurn.current = { turnId, message: text, forceWebSearch, kind: "text" };
    // sentTs is captured now (before any pre-send delay) so slowdown windows are
    // measured from when the participant pressed Send, matching desktop.
    const sentTs = Date.now();
    if (delayBeforeSendOn) {
      setStatus("delaying");
      delayTimerRef.current = window.setTimeout(() => {
        delayTimerRef.current = null;
        void runTurn(turnId, text, false, sentTs, forceWebSearch);
      }, delaySeconds * 1000);
    } else {
      void runTurn(turnId, text, false, sentTs, forceWebSearch);
    }
  };

  const onSend = () => startTurn(false);
  const onSearchWeb = () => startTurn(true);

  // Speak the server's last reply. The server reads its own stored reply, so we
  // send no text. A voice-off error latches voiceDisabled (fall back to text).
  const playReply = async () => {
    try {
      const blob = await requestTts(session);
      setLastTtsBlob(blob);
      ttsPlaybackRef.current?.stop();
      ttsPlaybackRef.current = playAudioBlob(blob);
    } catch (e) {
      if (e instanceof SessionApiError && e.code && VOICE_OFF_CODES.has(e.code)) {
        setVoiceDisabled(true);
      }
      // transient errors (e.g. no_reply) just skip speaking this turn
    }
  };

  const replayLast = () => {
    if (!lastTtsBlob) return;
    ttsPlaybackRef.current?.stop();
    ttsPlaybackRef.current = playAudioBlob(lastTtsBlob);
  };

  // Stop recording, transcribe, and send the transcript like a typed message.
  const stopAndTranscribe = async () => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (recordIntervalRef.current !== null) {
      window.clearInterval(recordIntervalRef.current);
      recordIntervalRef.current = null;
    }
    if (!rec) {
      setRecordState("idle");
      return;
    }
    setRecordState("transcribing");
    let blob: Blob;
    try {
      blob = await rec.stop();
    } catch {
      setRecordState("idle");
      return;
    }
    try {
      const { text } = await transcribeAudio(session, blob);
      setRecordState("idle");
      const t = (text || "").trim();
      if (t) startTurn(false, t);
      else pushMsg("system", "Could not understand the audio. Please try again.");
    } catch {
      setRecordState("idle");
      pushMsg("system", "Voice transcription failed. Please type your message.");
    }
  };

  const onRecordToggle = async () => {
    if (recordState === "recording") {
      void stopAndTranscribe();
      return;
    }
    if (recordState !== "idle" || status !== "idle") return;
    const rec = new VoiceRecorder();
    try {
      await rec.start(() => void stopAndTranscribe(), MAX_RECORDING_MS);
    } catch (e) {
      if (e instanceof MicPermissionError) setMicDenied(true);
      return;
    }
    recorderRef.current = rec;
    setRecordState("recording");
    setRecordSecs(0);
    recordIntervalRef.current = window.setInterval(
      () => setRecordSecs((s) => s + 1),
      1000,
    );
  };

  const onRetry = () => {
    const p = pendingTurn.current;
    if (!p) {
      setStatus("idle");
      return;
    }
    // Re-run the SAME kind with the SAME turnId: the backend replays the cached
    // result (no re-charge) for a completed turn, or runs fresh for a cleared one.
    // A/B must re-run the A/B path so the already-charged generate is recovered.
    if (p.kind === "ab") void runAbTurn(p.turnId, p.message);
    else void runTurn(p.turnId, p.message, false, Date.now(), p.forceWebSearch);
  };

  const budgetDepleted = tokenBudgetOn && tokensUsed >= tokenBudgetAmount;
  const busy =
    status === "sending" ||
    status === "delaying" ||
    status === "blocked" ||
    status === "surveying" ||
    status === "ab" ||
    surveyActive ||
    abTrial !== null ||
    budgetDepleted;

  // Id of the newest assistant message (only that one gets a replay control,
  // since the server keeps just the last reply's audio).
  const lastAssistantId = messages.reduce<number | undefined>(
    (acc, m) => (m.role === "assistant" ? m.id : acc),
    undefined,
  );
  const voiceActive = voiceOn && !voiceDisabled;

  // Timed mid-chat surveys: poll the block clock, but only while the chat is
  // idle (the participant could type right now). The poll is torn down while a
  // reply is in progress, the input is locked, or another survey / A/B dialog
  // is up, and re-armed with an immediate check the moment the chat is idle
  // again, so a survey that came due mid-reply shows as soon as the reply
  // finishes. Mirrors desktop _check_time_survey.
  const chatIdle = status === "idle" && !busy && recordState === "idle";
  useEffect(() => {
    if (timeTriggers.length === 0 || !chatIdle) return;
    const check = () => {
      if (inFlightRef.current) return;
      const elapsedS = (Date.now() - sessionStartRef.current) / 1000;
      const i = nextDueTimeTrigger(timeTriggers, firedTimeTriggersRef.current, elapsedS);
      if (i < 0) return;
      firedTimeTriggersRef.current.add(i);
      showSurvey("resume", timeTriggers[i]);
    };
    check();
    const iv = window.setInterval(check, 500);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatIdle, timeTriggers.length]);

  const handleSurveyClose = () => {
    setSurveyActive(false);
    setActiveTimeTrigger(null);
    const after = pendingSurveyAfter.current;
    pendingSurveyAfter.current = null;
    if (after === "advance") onDone({ kind: "advanced" });
    else setStatus("idle");
  };

  return (
    <div className="jol-chat">
      {tokenBudgetOn ? (
        <TokenBudgetDisplay
          used={tokensUsed}
          budget={tokenBudgetAmount}
          displayType={tokenDisplayType}
          settings={settings}
        />
      ) : null}

      {surveyActive ? (
        <InterTrialSurvey
          session={session}
          scrollLock={surveyScrollLock}
          onClose={handleSurveyClose}
          trigger={
            activeTimeTrigger
              ? { id: activeTimeTrigger.id, surveyId: activeTimeTrigger.surveyId }
              : undefined
          }
        />
      ) : null}

      {abTrial ? (
        <AbTestDialog
          promptMessage={abDialogMessage}
          optionA={abTrial.optionA}
          optionB={abTrial.optionB}
          rawA={abRawA}
          rawB={abRawB}
          defaultRevealDelayMs={abTrial.revealDelayMs}
          onSelect={onAbSelect}
        />
      ) : null}

      {showStart && startText ? (
        <div
          className="jol-chat-header"
          style={{ color: startColor, fontSize: `${startPx}px` }}
        >
          {startText}
        </div>
      ) : null}

      <div
        className="jol-chat-list"
        ref={listRef}
        style={scrollLocked ? { overflowY: "hidden" } : undefined}
      >
        {messages.map((m) =>
          m.role === "system" ? (
            <div
              key={m.id}
              className="jol-msg-row jol-msg-row--system"
              style={{
                textAlign: "center",
                color: "#868e96",
                fontSize: "0.85em",
                margin: "8px 0",
              }}
            >
              {m.text}
            </div>
          ) : m.role === "user" ? (
            <div key={m.id} className="jol-msg-row jol-msg-row--user">
              <div
                className="jol-bubble jol-bubble--user"
                style={{ fontSize: `${bubbleFontPx}px` }}
              >
                {m.text}
              </div>
            </div>
          ) : (
            <div key={m.id} className="jol-msg-row jol-msg-row--assistant">
              <div
                className="jol-bubble jol-bubble--assistant"
                style={{ fontSize: `${bubbleFontPx}px` }}
              >
                {voiceActive && voiceOnlyMode ? (
                  m.id === lastAssistantId && lastTtsBlob ? (
                    <button
                      type="button"
                      onClick={replayLast}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        font: "inherit",
                        color: "inherit",
                        padding: 0,
                      }}
                    >
                      {"\u{1F50A}"} Play voice message
                    </button>
                  ) : (
                    <span>{"\u{1F50A}"} Voice message</span>
                  )
                ) : (
                  <>
                    <AssistantMarkdown text={m.text} />
                    {voiceActive && m.id === lastAssistantId && lastTtsBlob ? (
                      <button
                        type="button"
                        onClick={replayLast}
                        aria-label="Replay voice"
                        title="Replay voice"
                        style={{
                          marginLeft: 8,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.9em",
                        }}
                      >
                        {"\u{1F50A}"}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ),
        )}

        {streamingMode && streamingText !== null ? (
          <div className="jol-msg-row jol-msg-row--assistant">
            <div
              className="jol-bubble jol-bubble--assistant"
              style={{ fontSize: `${bubbleFontPx}px` }}
            >
              {isFenceOpen(streamingText) ? (
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{streamingText}</pre>
              ) : (
                <AssistantMarkdown text={streamingText} />
              )}
            </div>
          </div>
        ) : null}

        {typewriterMode && typewriterText !== null ? (
          <div className="jol-msg-row jol-msg-row--assistant">
            <div
              className="jol-bubble jol-bubble--assistant"
              style={{ fontSize: `${bubbleFontPx}px` }}
            >
              <AssistantMarkdown text={typewriterText} />
            </div>
          </div>
        ) : null}

        {status === "sending" &&
        indicatorOn &&
        !(streamingMode && streamingText !== null) &&
        !(typewriterMode && typewriterText !== null) ? (
          <TypingIndicator
            label={indicatorLabel}
            style={indicatorStyle}
            fontSizePx={bubbleFontPx}
            cycle={cycle}
          />
        ) : null}
      </div>

      {status === "error" ? (
        <div className="jol-chat-error">
          <span>{errorMsg}</span>
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}

      <div className="jol-chat-input">
        <textarea
          ref={inputRef}
          className="jol-input"
          rows={1}
          value={input}
          placeholder="Type your message..."
          disabled={busy}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <Button variant="primary" onClick={onSend} disabled={busy || !input.trim()}>
          Send
        </Button>
        {webSearchOn ? (
          <Button variant="secondary" onClick={onSearchWeb} disabled={busy || !input.trim()}>
            Search Web
          </Button>
        ) : null}
        {voiceOn && sttEnabled && !micDenied ? (
          <Button
            variant="secondary"
            onClick={onRecordToggle}
            disabled={recordState === "transcribing" || (recordState === "idle" && busy)}
          >
            {recordState === "recording"
              ? `Stop ${fmtSecs(recordSecs)}`
              : recordState === "transcribing"
                ? "Transcribing..."
                : "Record"}
          </Button>
        ) : null}
      </div>
      {voiceOn && sttEnabled && !micDenied ? (
        <div style={{ fontSize: 12, color: "#868e96", marginTop: 6 }}>
          Recording stops automatically after 10 minutes.
        </div>
      ) : null}
      {voiceOn && sttEnabled && micDenied ? (
        <div style={{ fontSize: 12, color: "#868e96", marginTop: 6 }}>
          Microphone access was blocked. Please type your message instead.
        </div>
      ) : null}
    </div>
  );
}
